import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useState } from 'react'

import { useStocksServiceGetInternationalNameListEndpointKey } from 'src/api/queries'
import {
  GetInternationalNameListResponse,
  GetSubstanceListResponseItem,
  StocksService
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

export const ProductsNameSelect = (props: SelectProps) => {
  const { t } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetInternationalNameListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInternationalNameListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        undefined,
        debouncedSearch
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetInternationalNameListResponse
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.map((item: GetSubstanceListResponseItem) => ({
        value: item.id,
        label: item?.name
      })) || []
  )

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      mode="multiple"
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      placeholder={t('common.international_name')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        props.loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
